// What the brand corner shows while the page loads — pure, so it can be tested
// without a browser (tests/brandState.test.js).
//
// Three moments, and each has its own picture:
//
//   pending   nothing is known yet: the first visit to this instance from this
//             browser, with the request still in flight. A skeleton the size
//             and shape of what is coming, never the product's «G» nor
//             «General ERP», which would be replaced half a second later.
//   known     a copy from an earlier visit, or the answer: painted from the
//             first frame and revalidated behind.
//   given up  no copy and no answer (error, timeout): the initials and the
//             name it has, rather than a skeleton that shimmers for ever.

export const BRAND_STORAGE_KEY = 'general.brand'
export const PRODUCT_NAME = 'General ERP'

/**
 * Where this instance keeps its copy. Two instances can share an origin —
 * served under sub-paths of one domain (APP_BASE) — and therefore share
 * localStorage: with one key, opening the second painted the first one's
 * brand. The root keeps the historical key, so nobody loses their copy.
 * index.html computes the same thing before the first paint: change one,
 * change the other.
 */
export function brandStorageKey(base = '/') {
  const path = base.endsWith('/') ? base : `${base}/`
  return path === '/' ? BRAND_STORAGE_KEY : `${BRAND_STORAGE_KEY}@${path}`
}

/** The copy an earlier visit left, or null — never throws (private mode, junk). */
export function readStoredBrand(storage, key) {
  try {
    const stored = JSON.parse(storage?.getItem(key) || 'null')
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * The corner, worked out.
 *
 * @param {object|null} brand      the public brand (stored copy or fresh answer)
 * @param {object} options
 * @param {boolean} options.settled  this page load already has an answer or gave up
 * @param {'logo'|'icon'} options.kind  the company logo (menu) or the app icon (door)
 * @param {(file: string, version?: string) => string} options.asset  brandAsset
 * @returns {{ pending: boolean, namePending: boolean, src: string|null, initials: string, name: string }}
 */
export function brandView(brand, { settled, kind, asset }) {
  const unknown = !brand && !settled
  // A copy saved before `hasLogo` existed says nothing about the logo: the
  // mark waits for the answer instead of guessing initials and switching.
  const logoUnknown = kind === 'logo' && Boolean(brand) && typeof brand.hasLogo !== 'boolean' && !settled

  let src = null
  if (brand && kind === 'icon') src = asset('icon.svg', brand.version)
  if (brand && kind === 'logo' && brand.hasLogo === true) src = asset('logo', brand.version)

  return {
    pending: unknown || logoUnknown,
    namePending: unknown,
    src,
    initials: (typeof brand?.initials === 'string' && brand.initials) || PRODUCT_NAME.charAt(0),
    name: (typeof brand?.name === 'string' && brand.name) || PRODUCT_NAME,
  }
}
